import React, { useState } from 'react';
import Box from '@mui/material/Box';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ListItemIcon,
} from '@mui/material';
import { ChevronDown, Check } from 'lucide-mui';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../reducers';
import planFeatures, { FeatureKey } from './featureLimits';

interface PlanDetailsProps {
  selectedBillingOption: 'monthly' | 'annual';
}

const PlanDetails: React.FC<PlanDetailsProps> = ({ selectedBillingOption }) => {
  const { plan } = useSelector((state: RootState) => state);
  const { sourceFeatureKey, selectedPlan } = plan.upgrade;
  const [expandedItemKey, setExpandedItemKey] = useState<FeatureKey | null>(
    sourceFeatureKey || 'AISummaries'
  );

  const toggleExpand = (feature: FeatureKey) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedItemKey(isExpanded ? feature : null);
  };

  const allFeatures = Object.keys(
    planFeatures[selectedPlan] || {}
  ) as FeatureKey[];

  const reorderedFeatures = sourceFeatureKey
    ? [
        sourceFeatureKey,
        ...allFeatures.filter((feature) => feature !== sourceFeatureKey),
      ]
    : allFeatures;

  const topRankedFeatures = reorderedFeatures
    .filter((feature) => feature !== sourceFeatureKey)
    .sort((a, b) => {
      const rankA = planFeatures[selectedPlan]?.[a]?.ranking || 100;
      const rankB = planFeatures[selectedPlan]?.[b]?.ranking || 100;
      return rankA - rankB;
    })
    .slice(0, 8);

  const displayedFeatures = sourceFeatureKey
    ? [sourceFeatureKey, ...topRankedFeatures]
    : topRankedFeatures;

  return (
    <Box
      sx={{
        width: '100%',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      {displayedFeatures
        .filter((feature) => !!planFeatures[selectedPlan]?.[feature])
        .map((feature: FeatureKey) => {
          const { title, description, annualtitle, annualDescription } =
            planFeatures[selectedPlan]?.[feature] || {};
          const isExpanded = expandedItemKey === feature;
          return (
            <Accordion
              key={feature}
              expanded={isExpanded}
              onChange={toggleExpand(feature)}
              sx={{
                boxShadow: 'none',
                border: 'none',
                borderRadius: '8px !important',
                backgroundColor: 'transparent',
                transition:
                  'background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                '&::before': {
                  display: 'none',
                },
                margin: '0 !important',
                willChange: 'background-color',
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ChevronDown
                    sx={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                      transition:
                        'transform 0.15s cubic-bezier(0.4, 0, 0.6, 1)',
                      willChange: 'transform',
                    }}
                  />
                }
                aria-controls={`${feature}-content`}
                id={`${feature}-header`}
                sx={{
                  padding: 0,
                  minHeight: '40px',
                  '& .MuiAccordionSummary-content': {
                    margin: '6px 0',
                    transition: 'margin 0.15s cubic-bezier(0.4, 0, 0.6, 1)',
                    willChange: 'margin',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 'unset',
                    marginRight: '8px',
                    '& svg': {
                      fontSize: isExpanded ? '1.2rem' : '1rem',
                      color: isExpanded
                        ? '#2e7d32 !important'
                        : 'text.secondary',
                      transition:
                        'font-size 0.15s cubic-bezier(0.4, 0, 0.6, 1), color 0.15s cubic-bezier(0.4, 0, 0.6, 1)',
                      willChange: 'font-size, color',
                    },
                  }}
                >
                  <Check />
                </ListItemIcon>
                <Box
                  component="span"
                  sx={{
                    fontSize: isExpanded ? '0.975rem' : '0.9rem',
                    fontWeight: isExpanded ? 600 : 400,
                    color: isExpanded ? 'text.primary' : 'text.secondary',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.6, 1)',
                    willChange: 'font-size, font-weight, color',
                  }}
                >
                  {selectedBillingOption === 'annual' && annualtitle
                    ? annualtitle
                    : title}
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: '0 0 12px 28px',
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                }}
              >
                {selectedBillingOption === 'annual' && annualDescription
                  ? annualDescription
                  : description}
              </AccordionDetails>
            </Accordion>
          );
        })}
    </Box>
  );
};

export default PlanDetails;
